import styled from '@emotion/styled';
import { Select, Table } from 'antd';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import TileButton from 'components/atoms/TileButton';
import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { subHeaderClass, yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchLandscapeValues } from 'hooks/promoLandscape/useFetchLandscapeValues';
import _ from 'lodash';
import { promoLandScapeDownload } from 'parser/graphToExcelDownloadParser';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { getPromoLandscapeColumns } from 'utils/getPromoLandscapeColumns';
import {
    calculateContainerHeight,
    calculateFontSize,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
    parseAttributeType,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import SelectionDropDown from '../molecules/SelectionDropDown';
import {
    graphXAxisLabelFontSize,
    groupBarChartWidth,
} from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PROMO_LANDSCAPE_ATTRIBUTE_TYPES_DROPDOWN_CHANGED,
    PROMO_LANDSCAPE_CHART_COPY,
    PROMO_LANDSCAPE_CHART_EXPORT,
    PROMO_LANDSCAPE_INVESTMENT_DROPDOWN_CHANGED,
    PROMO_LANDSCAPE_PROMO_DROPDOWN_CHANGED,
    PROMO_LANDSCAPE_TABLE_COPY,
    PROMO_LANDSCAPE_TABLE_TAB_CHANGED,
} from 'utils/mixpanelEventNames';
import DownSvg from 'assets/svg/Down';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { roiTrackerUrls } from 'config/urls';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { useFetchLandscapeGraphTableHandler } from 'hooks/promoLandscape/useFetchLandscapeGraphTableRQ';
import { promoLandscapeChartParser, promoLandscapeTableParser } from 'parser/promoLandscapeParser';
import { useGetCurrencyForTenant } from 'hooks/general/useCurrencyForClient';
import { queryClient } from 'services/reactQueryService';

export const height = Math.max(290, calculateContainerHeight(2, 33 + 16 * 2) - 200); //adding a buffer of 200

export const scoreHeaderClass = 'text-greyFont text-[15px] font-normal';
export const commonClassName = 'border  border-borderLinePurpuleAlt p-4 rounded-lg';
export const commonHeaderClass = 'font-medium font-xl m-0 text-headerColor';
const graphItemContainerClass = 'p-4 mb-4 border border-borderLinePurpule rounded mt-4';
export const absTileData = [
    { key: 'cy', label: 'CY' },
    { key: 'ya', label: 'YA' },
    { key: 'absChange', label: 'Change' },
];

const CustomTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        height: 44px;
        padding: 0px;
    }

    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 45px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border: ${'1px solid #DBDFE9'};
`;

const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

const dropdownLabelClass = 'text-lightBlack font-xs font-medium mb-1';

const yAxisGraphWidth = calculateFontSize(window.innerWidth, 60);
const tableId = `landscape-table-wrapper`;
const graphId = `landscape-grouped-chart`;
const totalChartContainer = 'promo-landscape-total-chart';
const mediaLabel = 'Media Investment';

const investmentDropdownList = [
    { label: 'Promo Investment', value: 'Promo Investment' },
    { label: mediaLabel, value: mediaLabel },
];

function PromoLandscape() {
    const containerWidth = useContainerWidth({
        containerId: graphId,
        buffer: yAxisGraphWidth + 65 * 2 + 34 * 2,
    }); //subtracting graph width and padding on 2 sides
    const refOfGraph = useRef<any>();
    const refOfTable = useRef<any>();

    const { appConfig } = useContext(UserContext);
    const promoLandscapeConfig = getConfig('promoLandscape', appConfig);
    const defaultAttributeTypeIndex = promoLandscapeConfig.defaultAttributeTypeIndex;
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForNonPercentage } = commonConfig;
    const showInvestmentDropdown = promoLandscapeConfig?.showInvestmentDropdown;
    const mediaLevelsDropdown = promoLandscapeConfig?.mediaLevelsDropdown;
    const roiLabel = promoLandscapeConfig?.roiLabel;
    const defaultInvestmentTypeItems = promoLandscapeConfig?.defaultInvestmentTypeItems || [];
    const overridePromoTacticDropdownName = promoLandscapeConfig?.overridePromoTacticDropdownName;
    const {
        dimensionConfig: { excludedDimensions },
    } = promoLandscapeConfig;

    const [expandedIndex, setExpandedIndex] = useState<null | number>(null);
    const [absTile, setAbsTile] = useState(absTileData[0]);
    const [attributeTypesList, setAttributeTypesList] = useState<any[]>([]);
    const [promoTypes, setPromoTypes] = useState<any[]>([]);
    const [investmentList, setInvestmentList] = useState<any[]>(investmentDropdownList);
    const [selectedInvestmentList, setSelectedInvestmentList] = useState<any[]>(
        showInvestmentDropdown ? [investmentList[1]?.label] : [investmentList[0]?.label],
    );
    const [selectedPromoTypes, setSelectedPromoTypes] = useState<any[]>([]);
    const [selectPPG, setSelectPPG] = useState(attributeTypesList.length ? attributeTypesList[8] : null);

    //react query variables
    const [promoLandscapeGraphTableVariables, setPromoLandscapeGraphTableVariables] = useState<any>({});
    const widthRef = useRef(0);
    const offsetRef = useRef(commonConfig.offset);

    //filter context
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodSelection,
        idForRestOfComponentLoading,
        filterNullError,
        timePeriodLoading,
        allDimAndAttributeType,
    } = useContext(FilterContextData);

    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            idForRestOfComponent,
    );

    const promoLandscapeLegends = [
        { label: 'YA Investment', color: colorConstants.blue },
        { label: 'CY Investment', color: colorConstants.cyan },
        {
            label: `YA ${roiLabel}`,
            color: colorConstants.lightGreenAlt2,
            style: { borderRadius: '100%', height: `8px`, width: '8px' },
        },
        {
            label: `CY ${roiLabel}`,
            color: colorConstants.pink,
            style: { borderRadius: '100%', height: `8px`, width: '8px' },
        },
    ];

    const selectedInvestmentTypeWithValues = useMemo(() => {
        return promoTypes
            .filter((item) => selectedPromoTypes.includes(item?.label))
            .map((item) => {
                return {
                    investment_type: item?.investment_type,
                    investment_category: item?.investment_category,
                };
            });
    }, [selectedPromoTypes]);

    const investmentTypeDropdownVariables = {
        time_period: timePeriodSelection && JSON.parse(timePeriodSelection),
        filters: idForRestOfComponent,
        investment_categories: selectedInvestmentList?.map((item) => _.snakeCase(item)) ?? [],
    };

    const promoLandscapeGraphTableLocalVariables = {
        limit: promoLandscapeConfig.limit,
        offset: offsetRef.current,
        // lagList: commonConfig.lagList,
        filters: idForRestOfComponent,
        attribute_type: String(selectPPG?.key)?.toLowerCase(),
        dimension: selectPPG?.dimension,
        investment_types: selectedInvestmentTypeWithValues,
        time_period: timePeriodSelection && JSON.parse(timePeriodSelection),
    };

    //react query to fetch investment type values
    const { error: dropdownDataError, loading: dropdownDataLoading } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: enabledVariable },
        queryKey: ['roi-tracker-investment-type-dropdown', investmentTypeDropdownVariables],
        url: roiTrackerUrls.investmentTypeDropdownUrl,
        variables: {
            ...investmentTypeDropdownVariables,
        },
        parserFunction: (data: any) => {
            if (data?.length === 0) throw 'No Data';
            //if global filter is loading, then return
            if (allDimAndAttributeTypeList?.loading) return {};
            const temp = data?.map((item: any) => {
                return {
                    label: item?.investment_type,
                    investment_type: item?.investment_type,
                    value: item?.investment_type,
                    investment_category: item?.investment_category,
                };
            });
            setPromoTypes(temp);
            setSelectedPromoTypes(temp.map((item: any) => item.label));
            return {};
        },
    });

    //react query to fetch graph data
    const {
        data: promoLandscapeGraphData,
        error: promoLandscapeGraphError,
        loading: promoLandscapeGraphLoading,
        count: promoLandscapeCount,
    } = useFetchLandscapeGraphTableHandler({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                !dropdownDataLoading &&
                    selectPPG &&
                    enabledVariable &&
                    promoLandscapeGraphTableVariables?.investment_types?.length > 0,
            ),
        },
        queryKey: ['promo-landscape-graph-data', promoLandscapeGraphTableVariables],
        url: roiTrackerUrls.roiTrackerGraphUrl,
        variables: {
            ...promoLandscapeGraphTableVariables,
        },
        parserFunction: (data: any) => {
            if (data?.length === 0) throw 'No Data';
            return promoLandscapeChartParser(data, decimalForNonPercentage);
        },
    });

    //react query to fetch table data
    const {
        data: promoLandscapeTableData,
        error: promoLandscapeTableError,
        loading: promoLandscapeTableLoading,
    } = useFetchLandscapeGraphTableHandler({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                !dropdownDataLoading &&
                    selectPPG &&
                    enabledVariable &&
                    promoLandscapeGraphTableVariables?.investment_types?.length > 0,
            ),
        },
        queryKey: ['promo-landscape-table-data', promoLandscapeGraphTableVariables],
        url: roiTrackerUrls.roiTrackerTableUrl,
        variables: {
            ...promoLandscapeGraphTableVariables,
        },
        parserFunction: (data: any) => {
            if (data?.length === 0) throw 'No Data';
            return promoLandscapeTableParser(data, promoLandscapeConfig, appConfig);
        },
    });

    //used to debounce Loading state, so that UI wont flicker
    const { loading: combinedLoading } = useDebounceLoading({
        loadingDependency:
            allDimAndAttributeTypeList?.loading ||
            promoLandscapeGraphLoading ||
            allDimAndAttributeType.loading ||
            dropdownDataLoading,
    });

    const graphViewContainerWidth = Math.max(
        promoLandscapeGraphData?.chartData?.data
            ? (promoLandscapeGraphData?.chartData.data.length / 2) * groupBarChartWidth
            : 0,
        containerWidth,
    );
    const perGraphWidth = graphViewContainerWidth / (promoLandscapeGraphData?.chartData?.data?.length / 2);
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !selectPPG
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setAttributeTypesList(data);
                setSelectPPG(data?.[defaultAttributeTypeIndex]);
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);

    const memoisedColumns = useMemo(() => {
        return getPromoLandscapeColumns(promoLandscapeTableData?.tableData, expandedIndex, setExpandedIndex, absTile);
    }, [promoLandscapeTableData?.tableData, expandedIndex, absTile]);

    //manually close total graph tooltip when mouse is away
    useLayoutEffect(() => {
        const graphTotalSectionContainer = document.getElementById(totalChartContainer);
        function closeGraphTooltipOnMouseLeave() {
            //chart tooltip element
            const tooltipElem = Array.from(
                document.getElementsByClassName('g2-tooltip') as HTMLCollectionOf<HTMLElement>,
            );
            tooltipElem.forEach((elem) => {
                elem.style.visibility = 'hidden';
            });
        }
        graphTotalSectionContainer?.addEventListener('mouseleave', closeGraphTooltipOnMouseLeave);
        return () => {
            graphTotalSectionContainer?.removeEventListener('mouseleave', closeGraphTooltipOnMouseLeave);
        };
    }, [promoLandscapeGraphData, combinedLoading]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions) || [];

            let resultData = [...data];

            //for media investment, change the levels list
            if (selectedInvestmentList?.includes(mediaLabel)) {
                resultData = data?.filter((item: any) => {
                    return mediaLevelsDropdown.includes(item?.label);
                });
            }
            setAttributeTypesList(resultData);
            setSelectPPG(resultData?.[defaultAttributeTypeIndex]);
        }
        //call api to get updated investment type
    }, [selectedInvestmentList, allDimAndAttributeTypeList?.loading]);

    useEffect(() => {
        if (selectPPG && selectedPromoTypes?.length > 0) {
            offsetRef.current = 0;
            widthRef.current = 0;
            //react query
            setPromoLandscapeGraphTableVariables({
                ...promoLandscapeGraphTableLocalVariables,
                attribute_type: String(selectPPG?.key)?.toLowerCase(),
                dimension: selectPPG.dimension,
                offset: offsetRef.current,
            });
        }
    }, [idForRestOfComponent, timePeriodSelection, selectPPG, selectedInvestmentTypeWithValues]);

    // const combinedLoading =
    //     allDimAndAttributeTypeList?.loading ||
    //     loading ||
    //     allDimAndAttributeType.loading ||
    //     mechanicsTacticsDropdownLoading;

    const infiniteScrollOnNext = (width: number, loading: boolean) => {
        if (loading) return;
        widthRef.current = width || 0;
        const newOffset = offsetRef.current + promoLandscapeConfig.limit;

        //for react query
        setPromoLandscapeGraphTableVariables({
            ...promoLandscapeGraphTableVariables,
            offset: newOffset,
        });
        offsetRef.current = newOffset;
    };
    //debounced infinite scroller function
    // const debouncedInfiniteScroller = useCallback(_.debounce(infiniteScrollOnNext, 150), [
    //     timePeriodSelection,
    //     idForRestOfComponent,
    //     variables,
    //     selectPPG,
    //     selectedPromoTypes,
    // ]);
    //console.log(promoTypes, 'promoType');
    //listening to scroll

    const subHeader = `Investment vs ${roiLabel}`;
    useEffect(() => {
        const tableScrollableElement = document.getElementById(tableId)?.getElementsByClassName('ant-table-body')[0];
        const graphElem = document.getElementById(graphId);

        function scrollFunction(event: any) {
            if (event.target === graphElem) {
                // If graph is scrolled, scroll the table
                tableScrollableElement?.scrollTo?.({
                    left: Number(graphElem?.scrollLeft) * 1, // Giving a buffer because widths of table and chart are different
                    behavior: 'auto',
                });
            } else {
                // If table is scrolled, scroll the graph
                graphElem?.scrollTo?.({
                    left: Number(tableScrollableElement?.scrollLeft) * 1,
                    behavior: 'auto',
                });
            }
        }

        if (promoLandscapeGraphData?.chartData?.data?.length > 0) {
            // Add event listeners for both graph and table
            graphElem?.addEventListener?.('scroll', scrollFunction, { passive: true });
            tableScrollableElement?.addEventListener?.('scroll', scrollFunction, { passive: true });

            return () => {
                //when infinite scrolling, dont set left to 0
                if (offsetRef.current === 0) {
                    graphElem?.scrollTo({
                        left: 0,
                    });
                }
                // Cleanup: remove event listeners
                graphElem?.removeEventListener?.('scroll', scrollFunction);
                tableScrollableElement?.removeEventListener?.('scroll', scrollFunction);
            };
        }
    }, [promoLandscapeGraphData, combinedLoading]);

    function selectAllFunction(setSelectedPromoTypes: any, e: any) {
        trackEventMixpanel(PROMO_LANDSCAPE_PROMO_DROPDOWN_CHANGED, {
            dropdownValue: 'Select All',
        });
        setSelectedPromoTypes((oldData: any) => {
            let data = _.cloneDeep(oldData);
            if (e.target.checked) {
                let allPromo = promoTypes?.map((promo: any) => {
                    return promo.value;
                });
                return allPromo;
            } else {
                //    let filteredList = [promoTypes[0].value];
                let filteredList = promoTypes
                    ?.filter((item) => defaultInvestmentTypeItems?.includes(_.snakeCase(item?.label)))
                    ?.map((item) => item.value);
                return filteredList?.length > 0 ? filteredList : [promoTypes[0].value];
            }
        });
    }
    //tooltip custom items
    function createCustomItemForTooltip(items: any[]) {
        const additionalTooltipItems = items?.[0]?.data?.extraItems || [];
        let roiCyYaItem = {};
        let tooltipItem = [];
        if (items?.[0]?.name?.includes(roiLabel)) {
            //investment tooltip item
            roiCyYaItem = {
                color: items?.[0]?.data?.type === 'CY' ? colorConstants.cyan : colorConstants.blue,
                name: `${items?.[0]?.data?.type} Investment`,
                value: formatNumWithKMBAlt(items?.[0]?.data?.sales, decimalForNonPercentage, decimalForNonPercentage),
            };
            tooltipItem = [roiCyYaItem, ...items, ...additionalTooltipItems];
        } else {
            //roi tooltip item
            roiCyYaItem = {
                color: items?.[0]?.data?.type === 'CY' ? colorConstants.pink : colorConstants.lightGreenAlt2,
                name: `${items?.[0]?.data?.type} ${roiLabel}`,
                value: formatNumWithKMBAlt(items?.[0]?.data?.roi, decimalForNonPercentage, decimalForNonPercentage),
            };
            tooltipItem = [...items, roiCyYaItem, ...additionalTooltipItems];
        }

        return tooltipItem;
    }
    const currency = useGetCurrencyForTenant();

    const excelDownload = useMemo(() => {
        const headers = [selectPPG?.label, `Investment YA`, `Investment`, `${roiLabel} YA`, `${roiLabel}`];
        if (promoLandscapeGraphData?.chartData?.data?.length)
            return { headers, data: promoLandScapeDownload(promoLandscapeGraphData?.chartData?.data) };
        return { headers: [], data: [] };
    }, [promoLandscapeGraphData?.chartData?.data]);

    return (
        <section className={` ${commonClassName} promo-landscape-container bg-white`}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div>
                    <h2 className={commonHeaderClass}>{promoLandscapeConfig?.mainHeader}</h2>
                    <div className="flex justify-between items-center mt-2">
                        <div className="flex gap-3">
                            {showInvestmentDropdown && (
                                <div>
                                    <p className={dropdownLabelClass}>Investment Category</p>
                                    <SelectionDropDown
                                        className="dropdown-width-alt"
                                        list={investmentList}
                                        checkedListObject={selectedInvestmentList}
                                        disabled={combinedLoading}
                                        setSelectedList={setSelectedInvestmentList}
                                        selectedList={selectedInvestmentList}
                                        eventMixPanelString={PROMO_LANDSCAPE_INVESTMENT_DROPDOWN_CHANGED}
                                    />
                                </div>
                            )}

                            <div>
                                <p className={dropdownLabelClass}>
                                    {overridePromoTacticDropdownName || 'Promotion Tactic'}
                                </p>
                                <SelectionDropDown
                                    className="dropdown-width-alt"
                                    list={promoTypes}
                                    checkedListObject={selectedPromoTypes}
                                    eventMixPanelString={PROMO_LANDSCAPE_PROMO_DROPDOWN_CHANGED}
                                    disabled={combinedLoading}
                                    setSelectedList={setSelectedPromoTypes}
                                    selectedList={selectedPromoTypes}
                                    customSelectAllFunction={selectAllFunction}
                                />
                            </div>
                        </div>
                        <div>
                            <p className={dropdownLabelClass}>Level</p>
                            <CustomSelect
                                suffixIcon={<DownSvg />}
                                disabled={combinedLoading}
                                //loading={allDimAndAttributeTypeList?.loading}
                                value={selectPPG?.value}
                                options={attributeTypesList}
                                className="dropdown-height dropdown-width-alt uniqSelPLAttributeTypeDropdown"
                                onChange={(val: any, item: any) => {
                                    setSelectPPG(item);
                                    //mixpanel event
                                    trackEventMixpanel(PROMO_LANDSCAPE_ATTRIBUTE_TYPES_DROPDOWN_CHANGED, {
                                        dropdownName: item?.label,
                                    });
                                }}
                            />
                        </div>
                    </div>

                    {
                        // IF ERROR RETURN ERROR MESSAGE
                        (promoLandscapeGraphError || dropdownDataError) &&
                        !combinedLoading &&
                        !promoLandscapeGraphData ? (
                            <div
                                style={{
                                    height: height + 150,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <ErrorBox msg={String(promoLandscapeGraphError || dropdownDataError)} />
                            </div>
                        ) : // IF LOADING RETURN LOADER
                        combinedLoading ? (
                            <div
                                style={{
                                    height: height + 150,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Loader />
                            </div>
                        ) : (
                            <div
                                className={`${graphItemContainerClass}`}
                                style={{ minHeight: height }}
                                ref={refOfGraph}
                            >
                                <div className="flex justify-between">
                                    <p className={subHeaderClass}>{subHeader}</p>
                                    <div className="flex gap-2">
                                        <CopyToClipBoard
                                            refOfComponent={refOfGraph}
                                            mixpanelEvent={() => trackEventMixpanel(PROMO_LANDSCAPE_CHART_COPY)}
                                        />
                                        <DownloadExcel
                                            mixpanelEvent={() => trackEventMixpanel(PROMO_LANDSCAPE_CHART_EXPORT)}
                                            excelData={excelDownload?.data}
                                            headers={excelDownload?.headers}
                                            title={subHeader}
                                        />
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <Legend data={promoLandscapeLegends} />
                                </div>
                                <div className="flex justify-between ">
                                    <span className={`${yAxisLabelClass} ml-[65px]`}>Investments {currency}</span>
                                    <span className={`${yAxisLabelClass} mr-4`}>{roiLabel}</span>
                                </div>
                                <div>
                                    <div className="flex ml-1 mt-2 pl-[65px]">
                                        <Barchart
                                            // padding={[0, 0, 0, 48]}
                                            barWidth={31}
                                            xAxisConfig={false}
                                            dataset={[]}
                                            height={height}
                                            loading={false}
                                            chartContainer={`promo-landscape-y1-${1}`}
                                            fields={['category', 'sales']}
                                            minBound={scaleMinValue(
                                                30,
                                                promoLandscapeGraphData?.chartData?.investmentMinVal,
                                            )}
                                            maxBound={scaleMaxValue(
                                                30,
                                                promoLandscapeGraphData?.chartData?.investmentMaxVal,
                                            )}
                                            width={yAxisGraphWidth}
                                            colorKey={'type'}
                                            yAxisLabelFormatter={(val: any) => {
                                                return formatNumWithKMBAlt(
                                                    val,
                                                    decimalForNonPercentage,
                                                    decimalForNonPercentage,
                                                );
                                            }}
                                            // Yline
                                            YGrid={false}
                                        />
                                        <HorizontalInfiniteScroller
                                            id={graphId}
                                            hasMore={
                                                Math.max(offsetRef.current, 1) * promoLandscapeConfig.limit <
                                                    promoLandscapeCount || false
                                            } //dividing by 2 to remove grouped values
                                            scrollThreshold={0.9}
                                            toScrollWidth={widthRef.current}
                                            onNext={(width: number) => {
                                                infiniteScrollOnNext(width, promoLandscapeGraphLoading);
                                            }}
                                        >
                                            <div
                                                id={graphId}
                                                style={{
                                                    overflowX: 'auto',
                                                    display: 'flex',
                                                    maxWidth: '100%',
                                                    paddingBottom: 45,
                                                }}
                                            >
                                                {/* TOTAL BAR CHART INDEX - 0 */}
                                                <div
                                                    style={{
                                                        position: 'sticky',
                                                        left: 0,
                                                        background: 'white',
                                                        zIndex: 10,
                                                    }}
                                                    id="promo-landscape-total"
                                                >
                                                    <Barchart
                                                        tooltipConfig={{
                                                            customItems: (items: any) => {
                                                                return createCustomItemForTooltip(items);
                                                            },
                                                            shared: false,
                                                            enterable: true,
                                                            offset: 2,
                                                        }}
                                                        enablePointChartTooltip
                                                        enableGroupedPointChart
                                                        barWidth={15}
                                                        disableIntervalLabel={true}
                                                        // yAxisConfig={false}

                                                        dataset={
                                                            promoLandscapeGraphData?.chartData?.data?.length > 0
                                                                ? promoLandscapeGraphData.chartData.data.slice(0, 2)
                                                                : []
                                                        }
                                                        height={height}
                                                        //  showLegend
                                                        loading={false}
                                                        chartContainer={totalChartContainer}
                                                        fields={['category', 'sales', 'type', 'roi']}
                                                        pointMinBound={scaleMinValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.roiMinVal,
                                                        )}
                                                        pointMaxBound={scaleMaxValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.roiMaxVal,
                                                        )}
                                                        minBound={scaleMinValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.investmentMinVal,
                                                        )}
                                                        maxBound={scaleMaxValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.investmentMaxVal,
                                                        )}
                                                        width={Math.min(Number(perGraphWidth) || 100, 32767)} //here 32767 is the max width of the canvas in browser
                                                        // xAxisLabelFormatter={(val: any) => {
                                                        //     return addLineAndFormatString(
                                                        //         val,
                                                        //         Math.floor((perGraphWidth || 100) / 4.5), //got 4.5 when 73 default width is divided by 16 character
                                                        //     );
                                                        // }}
                                                        pointChartTooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: `${xAxis} ${roiLabel}`,
                                                                value: formatNumWithKMBAlt(
                                                                    val,
                                                                    decimalForNonPercentage,
                                                                    decimalForNonPercentage,
                                                                ),
                                                            };
                                                        }}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: `${xAxis} Investment`,
                                                                value: formatNumWithKMBAlt(
                                                                    val,
                                                                    decimalForNonPercentage,
                                                                    decimalForNonPercentage,
                                                                ),
                                                            };
                                                        }}
                                                        Xline={false}
                                                        colorKey={'type'}
                                                        enableGroupedChart
                                                        groupedPointChartTooltip={`type*roi`}
                                                        groupedChartColor={[colorConstants.blue, colorConstants.cyan]}
                                                        groupedPointColor={[
                                                            colorConstants.lightGreenAlt2,
                                                            colorConstants.pink,
                                                        ]}
                                                    />
                                                    <div
                                                        style={{
                                                            width: perGraphWidth,
                                                            minWidth: perGraphWidth,
                                                            overflowWrap: 'anywhere',
                                                            fontSize: graphXAxisLabelFontSize,
                                                        }}
                                                        className="text-center p-[2px]"
                                                    >
                                                        {promoLandscapeGraphData?.chartData?.data?.[0]?.category || ''}
                                                    </div>
                                                </div>
                                                <div>
                                                    <Barchart
                                                        tooltipConfig={{
                                                            customItems: (items: any) => {
                                                                return createCustomItemForTooltip(items);
                                                            },
                                                            shared: false,
                                                            enterable: true,
                                                            offset: 2,
                                                        }}
                                                        enablePointChartTooltip
                                                        enableGroupedPointChart
                                                        barWidth={15}
                                                        disableIntervalLabel={true}
                                                        // yAxisConfig={false}

                                                        dataset={
                                                            promoLandscapeGraphData?.chartData?.data?.length > 0
                                                                ? promoLandscapeGraphData?.chartData?.data?.slice(2)
                                                                : []
                                                        }
                                                        height={height}
                                                        //  showLegend
                                                        loading={false}
                                                        chartContainer={`promo-landscape-x-${1}`}
                                                        fields={['category', 'sales', 'type', 'roi']}
                                                        pointMinBound={scaleMinValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.roiMinVal,
                                                        )}
                                                        pointMaxBound={scaleMaxValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.roiMaxVal,
                                                        )}
                                                        minBound={scaleMinValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.investmentMinVal,
                                                        )}
                                                        maxBound={scaleMaxValue(
                                                            30,
                                                            promoLandscapeGraphData?.chartData?.investmentMaxVal,
                                                        )}
                                                        width={
                                                            perGraphWidth *
                                                                ((promoLandscapeGraphData?.chartData?.data?.length -
                                                                    2) /
                                                                    2) || //removing total data
                                                            window.innerWidth
                                                        }
                                                        // xAxisLabelFormatter={(val: any) => {
                                                        //     return addLineAndFormatString(
                                                        //         val,
                                                        //         Math.floor((perGraphWidth || 100) / 4.5), //got 4.5 when 73 default width is divided by 16 character
                                                        //     );
                                                        // }}
                                                        pointChartTooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: `${xAxis} ${roiLabel}`,
                                                                value: formatNumWithKMBAlt(
                                                                    val,
                                                                    decimalForNonPercentage,
                                                                    decimalForNonPercentage,
                                                                ),
                                                            };
                                                        }}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: `${xAxis} Investment`,
                                                                value: formatNumWithKMBAlt(
                                                                    val,
                                                                    decimalForNonPercentage,
                                                                    decimalForNonPercentage,
                                                                ),
                                                            };
                                                        }}
                                                        Xline={false}
                                                        colorKey={'type'}
                                                        enableGroupedChart
                                                        groupedPointChartTooltip={`type*roi`}
                                                        groupedChartColor={[colorConstants.blue, colorConstants.cyan]}
                                                        groupedPointColor={[
                                                            colorConstants.lightGreenAlt2,
                                                            colorConstants.pink,
                                                        ]}
                                                    />
                                                    <div className="flex">
                                                        {promoLandscapeGraphData?.chartData?.data
                                                            ?.slice?.(2)
                                                            ?.map((item: any, index: number) => {
                                                                if (index % 2 === 0)
                                                                    return (
                                                                        <div
                                                                            style={{
                                                                                width: perGraphWidth,
                                                                                minWidth: perGraphWidth,
                                                                                overflowWrap: 'anywhere',
                                                                                fontSize: graphXAxisLabelFontSize,
                                                                            }}
                                                                            className="text-center p-[2px]"
                                                                            key={item?.category}
                                                                        >
                                                                            {item?.category}
                                                                        </div>
                                                                    );
                                                            })}
                                                    </div>
                                                </div>
                                            </div>
                                        </HorizontalInfiniteScroller>

                                        {/* Y2 AXIS */}
                                        <div>
                                            <Barchart
                                                // padding={[0, 0, 0, 48]}
                                                padding={[0, 0, 0, 20]}
                                                barWidth={15}
                                                xAxisConfig={false}
                                                dataset={[]}
                                                height={height}
                                                loading={false}
                                                chartContainer={`promo-landscape-y2`}
                                                fields={['category', 'roi']}
                                                minBound={scaleMinValue(
                                                    30,
                                                    promoLandscapeGraphData?.chartData?.roiMinVal,
                                                )}
                                                maxBound={scaleMaxValue(
                                                    30,
                                                    promoLandscapeGraphData?.chartData?.roiMaxVal,
                                                )}
                                                width={yAxisGraphWidth}
                                                colorKey={'type'}
                                                yAxisLabelFormatter={(val: any) => {
                                                    return formatNumWithKMBAlt(
                                                        val,
                                                        decimalForNonPercentage,
                                                        decimalForNonPercentage,
                                                    );
                                                }}
                                                // Yline
                                                YGrid={false}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <p className={`text-center !top-[-35px] mb-[-35px] relative ${yAxisLabelClass}`}>
                                    {selectPPG?.label}
                                </p>
                            </div>
                        )
                    }
                    <div ref={refOfTable}>
                        <div className="flex justify-between items-center mt-4 mb-2">
                            <TileButton
                                disabled={combinedLoading}
                                data={absTileData}
                                activeKey={absTile.key}
                                onClick={(item: any) => {
                                    trackEventMixpanel(PROMO_LANDSCAPE_TABLE_TAB_CHANGED, {
                                        tabName: item.label,
                                    });
                                    setAbsTile(item);
                                }}
                                style={{ marginRight: 16 }}
                            />
                            <CopyToClipBoard
                                mixpanelEvent={() => trackEventMixpanel(PROMO_LANDSCAPE_TABLE_COPY)}
                                refOfComponent={refOfTable}
                            />
                        </div>
                        <div id={tableId}>
                            <CustomTable
                                columns={memoisedColumns}
                                pagination={false}
                                scroll={{ x: '100%', y: height + 100 }}
                                loading={combinedLoading || promoLandscapeTableLoading}
                                dataSource={promoLandscapeTableData?.tableData}
                            />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

export default PromoLandscape;
